import React, { useEffect, useState } from 'react';
import { collection, query, where, getDocs, getDoc, updateDoc, doc, Timestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../Auth/Auth';
import { toast } from 'react-hot-toast';
import { Button, Card, Badge, Modal, EmptyState, SkeletonLoader } from '../UI';
import logger from '../../utils/logger';
import {
  ClipboardDocumentListIcon,
  PlayIcon,
  CheckCircleIcon,
  ClockIcon,
  CalendarIcon,
  UserIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CheckBadgeIcon,
  ExclamationCircleIcon
} from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleSolid } from '@heroicons/react/24/solid';

const MechanicTasks = () => {
  const [activeTasks, setActiveTasks] = useState([]);
  const [historicalTasks, setHistoricalTasks] = useState([]);
  const [selectedTask, setSelectedTask] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [tasksPerPage] = useState(5);
  const [showModal, setShowModal] = useState(false);
  const [verificationComments, setVerificationComments] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const { user, role } = useAuth();

  useEffect(() => {
    const fetchTasks = async () => {
      setIsLoading(true);
      if (user && (role === 'Admin' || role === 'Service Advisor' || role === 'Mechanic')) {
        try {
          const q = query(collection(db, "tasks"), where("assignedTo", "==", user.uid));
          const querySnapshot = await getDocs(q);
          const tasks = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          setActiveTasks(tasks.filter(task => task.status === 'new' || task.status === 'in-progress'));
          setHistoricalTasks(tasks.filter(task => task.status === 'completed'));
        } catch (error) {
          logger.error('Error fetching tasks:', error);
          toast.error('Failed to load tasks');
        }
      }
      setIsLoading(false);
    };
    fetchTasks();
  }, [user, role]);

  const startTask = async (taskId) => {
    const taskRef = doc(db, "tasks", taskId);
    const taskSnapshot = await getDoc(taskRef);
    if (taskSnapshot.exists() && taskSnapshot.data().status === 'new') {
      try {
        await updateDoc(taskRef, {
          status: 'in-progress',
          startedAt: Timestamp.now()
        });
        toast.success('Task started successfully');

        // Update state
        const updatedTask = { ...taskSnapshot.data(), id: taskId, status: 'in-progress', startedAt: Timestamp.now() };
        setActiveTasks(activeTasks.map(task => task.id === taskId ? updatedTask : task));
        setSelectedTask(updatedTask);
      } catch (error) {
        logger.error('Error starting task:', error);
        toast.error('Failed to start task');
      }
    } else {
      toast.info('Task has already been started or completed');
    }
  };

  const completeTask = async (taskId) => {
    const taskRef = doc(db, "tasks", taskId);
    try {
      await updateDoc(taskRef, {
        status: 'completed',
        completedAt: Timestamp.now()
      });
      toast.success('Task completed successfully');

      // Move task from active to historical
      const completedTask = activeTasks.find(task => task.id === taskId);
      if (completedTask) {
        setActiveTasks(activeTasks.filter(task => task.id !== taskId));
        setHistoricalTasks([{ ...completedTask, status: 'completed', completedAt: Timestamp.now() }, ...historicalTasks]);
      }
      setSelectedTask(null);
    } catch (error) {
      logger.error('Error completing task:', error);
      toast.error('Failed to complete task');
    }
  };

  const handleValidateTask = async (taskId) => {
    if (!verificationComments.trim()) {
      toast.error('Please add verification comments');
      return;
    }

    const taskRef = doc(db, "tasks", taskId);
    try {
      await updateDoc(taskRef, {
        taskVerified: true,
        verifiedBy: user.email,
        verifiedAt: Timestamp.now(),
        verificationComments
      });
      toast.success('Task validated successfully');
      setShowModal(false);
      setVerificationComments('');

      // Update state
      const updatedTasks = [...activeTasks, ...historicalTasks].map(task =>
        task.id === taskId ? { ...task, taskVerified: true, verificationComments, verifiedBy: user.email } : task
      );
      setActiveTasks(updatedTasks.filter(task => task.status === 'new' || task.status === 'in-progress'));
      setHistoricalTasks(updatedTasks.filter(task => task.status === 'completed'));

      if (selectedTask?.id === taskId) {
        setSelectedTask({ ...selectedTask, taskVerified: true, verificationComments, verifiedBy: user.email });
      }
    } catch (error) {
      logger.error('Error validating task:', error);
      toast.error('Failed to validate task');
    }
  };

  const updateSubtaskStatus = async (taskId, subtaskId, newStatus) => {
    const taskRef = doc(db, "tasks", taskId);
    try {
      const taskSnapshot = await getDoc(taskRef);
      if (taskSnapshot.exists()) {
        let taskData = taskSnapshot.data();
        const updatedSubtasks = taskData.subtasks.map(subtask =>
          subtask.id === subtaskId ? { ...subtask, status: newStatus } : subtask
        );

        const allCompleted = updatedSubtasks.every(subtask => subtask.status === 'completed');

        const updates = {
          subtasks: updatedSubtasks,
          updatedAt: Timestamp.now(),
          ...(allCompleted && { status: 'completed', completedAt: Timestamp.now() })
        };

        await updateDoc(taskRef, updates);
        toast.success('Subtask status updated successfully');

        // Update state
        const allTasks = [...activeTasks, ...historicalTasks];
        const updatedAllTasks = allTasks.map(task => task.id === taskId ? { ...task, ...updates } : task);

        setActiveTasks(updatedAllTasks.filter(task => task.status === 'new' || task.status === 'in-progress'));
        setHistoricalTasks(updatedAllTasks.filter(task => task.status === 'completed'));

        if (selectedTask?.id === taskId) {
          setSelectedTask({ ...selectedTask, ...updates });
        }

        if (allCompleted) {
          toast.success('All subtasks completed, task marked as completed');
        }
      } else {
        toast.error("Task not found");
      }
    } catch (error) {
      logger.error('Error updating subtask status:', error);
      toast.error('Failed to update subtask status');
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate();
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate();
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDateTime = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate();
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'new': { variant: 'info', label: 'New' },
      'in-progress': { variant: 'warning', label: 'In Progress' },
      'completed': { variant: 'success', label: 'Completed' },
      'pending': { variant: 'neutral', label: 'Pending' }
    };
    const config = statusConfig[status] || { variant: 'neutral', label: status };
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  // Pagination
  const indexOfLastTask = currentPage * tasksPerPage;
  const indexOfFirstTask = indexOfLastTask - tasksPerPage;
  const currentTasks = historicalTasks.slice(indexOfFirstTask, indexOfLastTask);
  const totalPages = Math.ceil(historicalTasks.length / tasksPerPage);

  const TaskCard = ({ task, onClick }) => {
    const isOverdue = task.deadline && task.status !== 'completed' && new Date() > task.deadline.toDate();

    return (
      <Card
        className="mb-4 hover:shadow-lg transition-all duration-200 cursor-pointer border-l-4"
        style={{ borderLeftColor: task.status === 'new' ? '#3B82F6' : '#F59E0B' }}
        onClick={onClick}
      >
        <div className="p-4">
          <div className="flex items-start justify-between mb-3">
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900 mb-1">{task.taskTitle}</h3>
              {task.jobCardNo && (
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Job Card:</span> {task.jobCardNo}
                </p>
              )}
            </div>
            <div className="ml-2">
              {getStatusBadge(task.status)}
            </div>
          </div>

          <div className="flex items-center gap-4 text-xs text-gray-600">
            {task.deadline && (
              <div className="flex items-center gap-1">
                <ClockIcon className="h-4 w-4" />
                <span className={isOverdue ? 'text-danger-600 font-medium' : ''}>
                  {formatDate(task.deadline)}
                </span>
              </div>
            )}
            {task.taskVerified && (
              <div className="flex items-center gap-1 text-success-600">
                <CheckBadgeIcon className="h-4 w-4" />
                <span>Verified</span>
              </div>
            )}
          </div>
        </div>
      </Card>
    );
  };

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <SkeletonLoader className="h-48 mb-6" />
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          <div className="lg:col-span-2">
            <SkeletonLoader className="h-96" />
          </div>
          <div className="lg:col-span-3">
            <SkeletonLoader className="h-96" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-6">
      {/* Header */}
      <Card className="mb-6 bg-gradient-to-r from-blue-50 to-indigo-50">
        <div className="px-6 py-5">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-blue-100 rounded-lg">
              <ClipboardDocumentListIcon className="h-6 w-6 text-blue-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">My Tasks</h1>
          </div>
          <p className="text-sm text-gray-600">Manage and track your assigned tasks</p>
        </div>
      </Card>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100">
          <div className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-blue-600">Active Tasks</p>
                <p className="text-3xl font-bold text-blue-900 mt-1">{activeTasks.length}</p>
              </div>
              <div className="p-3 bg-blue-200 rounded-full">
                <ClipboardDocumentListIcon className="h-8 w-8 text-blue-600" />
              </div>
            </div>
          </div>
        </Card>

        <Card className="bg-gradient-to-br from-green-50 to-green-100">
          <div className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-green-600">Completed</p>
                <p className="text-3xl font-bold text-green-900 mt-1">{historicalTasks.length}</p>
              </div>
              <div className="p-3 bg-green-200 rounded-full">
                <CheckCircleSolid className="h-8 w-8 text-green-600" />
              </div>
            </div>
          </div>
        </Card>

        <Card className="bg-gradient-to-br from-amber-50 to-amber-100">
          <div className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-amber-600">In Progress</p>
                <p className="text-3xl font-bold text-amber-900 mt-1">
                  {activeTasks.filter(t => t.status === 'in-progress').length}
                </p>
              </div>
              <div className="p-3 bg-amber-200 rounded-full">
                <ClockIcon className="h-8 w-8 text-amber-600" />
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Left Sidebar - Task Lists */}
        <div className="lg:col-span-2 space-y-6">
          {/* Active Tasks */}
          <Card>
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Active Tasks</h2>
              <p className="text-sm text-gray-600 mt-1">Tasks currently in progress or pending</p>
            </div>
            <div className="p-4 max-h-96 overflow-y-auto">
              {activeTasks.length === 0 ? (
                <EmptyState
                  title="No active tasks"
                  message="You don't have any active tasks at the moment."
                  icon={ClipboardDocumentListIcon}
                />
              ) : (
                activeTasks.map(task => (
                  <TaskCard
                    key={task.id}
                    task={task}
                    onClick={() => setSelectedTask(task)}
                  />
                ))
              )}
            </div>
          </Card>

          {/* Completed Tasks */}
          <Card>
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Completed Tasks</h2>
              <p className="text-sm text-gray-600 mt-1">Historical task records</p>
            </div>
            <div className="overflow-x-auto">
              {historicalTasks.length === 0 ? (
                <div className="p-4">
                  <EmptyState
                    title="No completed tasks"
                    message="Completed tasks will appear here."
                    icon={CheckCircleIcon}
                  />
                </div>
              ) : (
                <>
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                          Task
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                          Deadline
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                          Completed
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {currentTasks.map(task => (
                        <tr key={task.id} className="hover:bg-gray-50 transition-colors">
                          <td className="px-4 py-3 text-sm text-gray-900">
                            <div className="font-medium">{task.taskTitle}</div>
                            {task.jobCardNo && (
                              <div className="text-xs text-gray-500">{task.jobCardNo}</div>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                            {formatDate(task.deadline)}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                            {formatDate(task.completedAt)}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={() => setSelectedTask(task)}
                            >
                              View
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
                      <div className="flex items-center justify-between">
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                          disabled={currentPage === 1}
                        >
                          <ChevronLeftIcon className="h-4 w-4 mr-1" />
                          Previous
                        </Button>
                        <span className="text-sm text-gray-700">
                          Page {currentPage} of {totalPages}
                        </span>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                          disabled={currentPage === totalPages}
                        >
                          Next
                          <ChevronRightIcon className="h-4 w-4 ml-1" />
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </Card>
        </div>

        {/* Right Panel - Task Details */}
        <div className="lg:col-span-3">
          <Card className="h-full">
            {selectedTask ? (
              <div>
                {/* Task Header */}
                <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{selectedTask.taskTitle}</h3>
                      {selectedTask.jobCardNo && (
                        <p className="text-sm text-gray-600">
                          <span className="font-medium">Job Card:</span> {selectedTask.jobCardNo}
                        </p>
                      )}
                    </div>
                    {getStatusBadge(selectedTask.status)}
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-wrap gap-2">
                    {selectedTask.status === 'new' && (
                      <Button
                        variant="primary"
                        size="md"
                        onClick={() => startTask(selectedTask.id)}
                      >
                        <PlayIcon className="h-4 w-4 mr-2" />
                        Start Task
                      </Button>
                    )}
                    {selectedTask.status === 'in-progress' && (
                      <Button
                        variant="success"
                        size="md"
                        onClick={() => completeTask(selectedTask.id)}
                      >
                        <CheckCircleIcon className="h-4 w-4 mr-2" />
                        Complete Task
                      </Button>
                    )}
                    {!selectedTask.taskVerified && (role === 'Admin' || role === 'Service Advisor') && (
                      <Button
                        variant="secondary"
                        size="md"
                        onClick={() => setShowModal(true)}
                      >
                        <CheckBadgeIcon className="h-4 w-4 mr-2" />
                        Validate Task
                      </Button>
                    )}
                  </div>
                </div>

                {/* Task Details */}
                <div className="p-6 space-y-6">
                  {/* Timeline Information */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-3">
                      <div className="flex items-start gap-3">
                        <CalendarIcon className="h-5 w-5 text-gray-400 mt-0.5" />
                        <div>
                          <p className="text-xs font-medium text-gray-500 uppercase">Start By</p>
                          <p className="text-sm text-gray-900 font-medium">
                            {formatDate(selectedTask.startDate)}
                          </p>
                          <p className="text-xs text-gray-600">{formatTime(selectedTask.startDate)}</p>
                        </div>
                      </div>

                      <div className="flex items-start gap-3">
                        <ClockIcon className="h-5 w-5 text-gray-400 mt-0.5" />
                        <div>
                          <p className="text-xs font-medium text-gray-500 uppercase">Deadline</p>
                          <p className="text-sm text-gray-900 font-medium">
                            {formatDate(selectedTask.deadline)}
                          </p>
                          <p className="text-xs text-gray-600">{formatTime(selectedTask.deadline)}</p>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <div className="flex items-start gap-3">
                        <UserIcon className="h-5 w-5 text-gray-400 mt-0.5" />
                        <div>
                          <p className="text-xs font-medium text-gray-500 uppercase">Assigned By</p>
                          <p className="text-sm text-gray-900 font-medium">{selectedTask.assignedBy}</p>
                        </div>
                      </div>

                      {selectedTask.status === 'completed' && selectedTask.completedAt && (
                        <div className="flex items-start gap-3">
                          <CheckCircleIcon className="h-5 w-5 text-success-500 mt-0.5" />
                          <div>
                            <p className="text-xs font-medium text-gray-500 uppercase">Completed At</p>
                            <p className="text-sm text-gray-900 font-medium">
                              {formatDateTime(selectedTask.completedAt)}
                            </p>
                          </div>
                        </div>
                      )}

                      {selectedTask.taskVerified && (
                        <div className="flex items-start gap-3">
                          <CheckBadgeIcon className="h-5 w-5 text-success-500 mt-0.5" />
                          <div>
                            <p className="text-xs font-medium text-gray-500 uppercase">Verified By</p>
                            <p className="text-sm text-gray-900 font-medium">{selectedTask.verifiedBy}</p>
                            {selectedTask.verificationComments && (
                              <p className="text-xs text-gray-600 mt-1 italic">"{selectedTask.verificationComments}"</p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Subtasks */}
                  {selectedTask.subtasks && selectedTask.subtasks.length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold text-gray-900 mb-3 uppercase tracking-wide">
                        Subtasks ({selectedTask.subtasks.filter(s => s.status === 'completed').length}/{selectedTask.subtasks.length} Completed)
                      </h4>
                      <div className="space-y-2">
                        {selectedTask.subtasks.map((subtask, index) => (
                          <Card key={index} className="border border-gray-200">
                            <div className="p-4">
                              <div className="flex items-center justify-between gap-4">
                                <div className="flex-1">
                                  <p className="text-sm text-gray-900">{subtask.description}</p>
                                </div>
                                <div className="flex-shrink-0">
                                  <select
                                    value={subtask.status}
                                    onChange={(e) => updateSubtaskStatus(selectedTask.id, subtask.id, e.target.value)}
                                    disabled={selectedTask.taskVerified}
                                    className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                                  >
                                    <option value="pending">Pending</option>
                                    <option value="in-progress">In Progress</option>
                                    <option value="completed">Completed</option>
                                  </select>
                                </div>
                              </div>
                            </div>
                          </Card>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-full p-12">
                <EmptyState
                  title="No task selected"
                  message="Select a task from the list to view its details and manage subtasks."
                  icon={ClipboardDocumentListIcon}
                />
              </div>
            )}
          </Card>
        </div>
      </div>

      {/* Validation Modal */}
      <Modal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setVerificationComments('');
        }}
        title="Validate Task"
      >
        <div className="space-y-4">
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex gap-3">
              <ExclamationCircleIcon className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-blue-900">Task Verification</p>
                <p className="text-xs text-blue-700 mt-1">
                  Please review the completed task and provide your verification comments.
                </p>
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Verification Comments <span className="text-danger-500">*</span>
            </label>
            <textarea
              value={verificationComments}
              onChange={(e) => setVerificationComments(e.target.value)}
              placeholder="Enter your verification comments here..."
              rows={4}
              className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none"
            />
          </div>

          <div className="flex gap-3 justify-end pt-4 border-t border-gray-200">
            <Button
              variant="secondary"
              onClick={() => {
                setShowModal(false);
                setVerificationComments('');
              }}
            >
              Cancel
            </Button>
            <Button
              variant="success"
              onClick={() => handleValidateTask(selectedTask.id)}
            >
              <CheckBadgeIcon className="h-4 w-4 mr-2" />
              Submit Verification
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default MechanicTasks;
